import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Save, UserPlus, RotateCcw, Loader2 } from 'lucide-react';
import { API } from '../../../utils/api';
import { AiWorkoutForm } from './AiWorkoutForm';
import { AiDraftDay } from './AiDraftDay';
import { AiApplyDialog } from './AiApplyDialog';

const INITIAL_FORM = {
  goal: 'Hipertrofia', level: 'Intermediário', sex: '', days_per_week: 3,
  session_minutes: 60, focus: '', restrictions: '',
  reference_template_ids: [], reference_mode: 'next_phase',
};
const POLL_MS = 3000;
const MAX_POLLS = 220;

const errorMessage = (e, fallback) => {
  const detail = e.response?.data?.detail;
  if (Array.isArray(detail)) return detail[0]?.msg || fallback;
  return typeof detail === 'string' ? detail : fallback;
};

const toPayloadDays = (days) =>
  days.map((d) => ({
    name: d.name,
    exercises: d.exercises.map(({ exercise_id, execution_details, notes }) => ({ exercise_id, execution_details, notes })),
  }));

export const AiWorkoutTab = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => clearTimeout(pollRef.current), []);

  const poll = (id, count = 0) => {
    pollRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/admin/ai-workouts/drafts/${id}`);
        if (data.status === 'pending') {
          if (count < MAX_POLLS) return poll(id, count + 1);
          setGenerating(false);
          return toast.error('A geração demorou demais. Tente novamente.');
        }
        setGenerating(false);
        if (data.status === 'done') {
          setDraft(data.draft);
          toast.success('Treino gerado! Revise antes de salvar.');
        } else {
          toast.error(data.error || 'Não foi possível gerar o treino');
        }
      } catch (e) {
        setGenerating(false);
        toast.error('Erro ao consultar a geração');
      }
    }, POLL_MS);
  };

  const generate = async () => {
    setGenerating(true);
    setDraft(null);
    try {
      const payload = { ...form, sex: form.sex || null, focus: form.focus || null, restrictions: form.restrictions || null };
      const { data } = await axios.post(`${API}/admin/ai-workouts/generate`, payload);
      poll(data.id);
    } catch (e) {
      setGenerating(false);
      toast.error(errorMessage(e, 'Erro ao iniciar a geração'));
    }
  };

  const updateDay = (i, patch) =>
    setDraft((d) => ({ ...d, days: d.days.map((day, j) => (j === i ? { ...day, ...patch } : day)) }));

  const saveAsSaved = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/ai-workouts/save`, { days: toPayloadDays(draft.days), category: 'IA' });
      toast.success(`${data.saved} treino(s) salvos em "Treinos salvos" (categoria IA)`);
    } catch (e) {
      toast.error(errorMessage(e, 'Erro ao salvar'));
    } finally {
      setBusy(false);
    }
  };

  const applyToStudent = async ({ userId, start_date, end_date }) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/ai-workouts/apply/${userId}`, { days: toPayloadDays(draft.days), start_date, end_date });
      toast.success(`${data.applied} dia(s) aplicados para ${data.user_name}`);
      setApplyOpen(false);
    } catch (e) {
      toast.error(errorMessage(e, 'Erro ao aplicar'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-workout-tab">
      <div>
        <h2 className="font-primary font-black text-2xl text-white flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-neon-lime" /> Treino com IA
        </h2>
        <p className="text-zinc-500 text-sm mt-1">O Gemini monta um rascunho usando apenas exercícios do seu Banco. Você revisa e decide o que fazer.</p>
      </div>

      <AiWorkoutForm form={form} setForm={setForm} onSubmit={generate} loading={generating} />

      {generating && (
        <div className="flex items-center gap-3 text-zinc-400 text-sm bg-zinc-900/50 border border-neon-lime/20 rounded-2xl p-4" data-testid="ai-generating">
          <Loader2 className="w-5 h-5 animate-spin text-neon-lime" />
          Montando o treino... isso pode levar até 1 minuto.
        </div>
      )}

      {draft && (
        <div className="space-y-4" data-testid="ai-draft">
          <div className="bg-neon-lime/5 border border-neon-lime/20 rounded-2xl p-4">
            <h3 className="font-primary font-black text-lg text-white" data-testid="ai-draft-title">{draft.title}</h3>
            {draft.references?.length > 0 && (
              <p className="text-xs text-neon-lime/80 mt-1" data-testid="ai-draft-references">
                {draft.reference_mode === 'same_style' ? 'Mesmo estilo de' : 'Próxima fase de'}: {draft.references.join(', ')}
              </p>
            )}
            {draft.summary && <p className="text-zinc-400 text-sm mt-1">{draft.summary}</p>}
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {draft.days.map((day, i) => (
              <AiDraftDay key={i} day={day} dayIndex={i} onUpdate={(patch) => updateDay(i, patch)} />
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={saveAsSaved} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-neon-lime text-black font-bold text-sm disabled:opacity-50" data-testid="ai-save-btn">
              <Save className="w-4 h-4" /> Salvar em Treinos salvos
            </button>
            <button onClick={() => setApplyOpen(true)} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-neon-lime/40 text-neon-lime font-bold text-sm hover:bg-neon-lime/10 transition-colors duration-200 disabled:opacity-50" data-testid="ai-apply-btn">
              <UserPlus className="w-4 h-4" /> Aplicar a um aluno
            </button>
            <button onClick={generate} disabled={generating || busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-zinc-400 hover:text-white text-sm transition-colors duration-200" data-testid="ai-regenerate-btn">
              <RotateCcw className="w-4 h-4" /> Gerar novamente
            </button>
          </div>
        </div>
      )}

      <AiApplyDialog open={applyOpen} onClose={() => setApplyOpen(false)} onConfirm={applyToStudent} busy={busy} />
    </div>
  );
};

export default AiWorkoutTab;
